// src/hooks/useEnterpriseData.js
//
// T1.3 — the enterprise console's bridge to real warehouse data.
// Calls getEnterpriseSnapshot (T1.2) on the region-locked functions instance.
// DESIGN RULE: never throw to the UI, never blank the console — on error,
// empty result, missing org, or an org under the privacy floor, the caller
// falls back to the edition's modeled example (source: 'demo'), which is the
// sales demo working exactly as before.

import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase-config';

export default function useEnterpriseData(orgId, groupId, windowDays = 30) {
    const [state, setState] = useState({ data: null, loading: !!orgId, error: null });
    // Bumped by refresh(): after an upload the console re-reads the warehouse
    // without flashing back to the modeled example meanwhile.
    const [tick, setTick] = useState(0);

    useEffect(() => {
        if (!orgId) {
            setState({ data: null, loading: false, error: null });
            return undefined;
        }
        let cancelled = false;
        setState((s) => ({ data: tick ? s.data : null, loading: true, error: null }));
        (async () => {
            try {
                const fn = httpsCallable(functions, 'getEnterpriseSnapshot');
                const res = await fn({ orgId, groupId, windowDays });
                if (cancelled) return;
                const payload = res?.data;
                // Anything that isn't a confirmed live payload -> demo fallback.
                setState({
                    data: payload && payload.source === 'live' ? payload : null,
                    loading: false,
                    error: null
                });
            } catch (e) {
                console.error('useEnterpriseData fallback to demo:', e?.message || e);
                if (!cancelled) setState({ data: null, loading: false, error: e });
            }
        })();
        return () => { cancelled = true; };
    }, [orgId, groupId, windowDays, tick]);

    return {
        data: state.data,                          // live snapshot or null
        source: state.data ? 'live' : 'demo',
        loading: state.loading,
        error: state.error,
        refresh: () => setTick((t) => t + 1)       // re-read after an upload
    };
}
